import { useEffect, useMemo, useRef, useState } from 'react';
import { useRegistry } from '../apps/registryContext';
import { SYSTEM_LOGO_URL } from '../brand/systemLogo';
import { AppIcon } from './AppIcon';

interface StartMenuProps {
  onLaunch: (appId: string) => void;
}

export function StartMenu({ onLaunch }: StartMenuProps) {
  const registry = useRegistry();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    function onClickAway(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    window.addEventListener('mousedown', onClickAway);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onClickAway);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // design-v2:apps.yaml 数组顺序就是开始菜单顺序,无需再自行排序。
  const ordered = registry.entries;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return ordered;
    return ordered.filter((e) => e.title.toLowerCase().includes(q));
  }, [ordered, query]);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        aria-label="开始"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="os-taskbar-start"
      >
        <img src={SYSTEM_LOGO_URL} alt="Blade OS" />
      </button>
      {open && (
        <div role="menu" data-testid="start-menu" className="os-start-menu">
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="搜索 APP"
            aria-label="搜索 APP"
            className="os-start-menu-search"
          />
          <div className="os-start-menu-list">
            {filtered.length === 0 && (
              <div className="os-start-menu-empty">没有匹配的 APP</div>
            )}
            {filtered.map((entry) => (
              <button
                key={entry.id}
                type="button"
                role="menuitem"
                data-app-id={entry.id}
                onClick={() => {
                  setOpen(false);
                  onLaunch(entry.id);
                }}
                className="os-start-menu-item"
              >
                <span className="os-start-menu-icon">
                  <AppIcon icon={entry.icon} title={entry.title} size={24} appId={entry.id} />
                </span>
                <span className="flex-1">{entry.title}</span>
              </button>
            ))}
          </div>
          <div className="os-start-menu-footer" title={`Blade OS ${__APP_VERSION__}`}>
            <span>Blade OS</span>
            <span data-testid="app-version">{__APP_VERSION__}</span>
          </div>
        </div>
      )}
    </div>
  );
}
